/**
 * fieldValidation.ts
 *
 * Core validation logic for form fields.
 * Provides comprehensive validation for all field types with error handling.
 *
 * Validation Features:
 * - Required field checks
 * - Pattern matching
 * - Type validation
 * - Length constraints
 * - Range validation
 * - Format validation
 *
 * Supported Formats:
 * - Email addresses
 * - Phone numbers
 * - URLs
 * - ZIP codes
 * - Usernames
 * - Passwords
 * - Names
 * - Alphanumeric
 *
 * Number Formats:
 * - Integers
 * - Decimals
 * - Currency
 * - Percentages
 *
 * Error Handling:
 * - Detailed error messages
 * - Multiple error support
 * - Validation result types
 * - Type-safe validation
 */

import { Field } from "../types/types";
import { ValidationPatterns } from "./ValidationPatterns";
import { NumberFormatConfig, NumberFormatType, BaseValidationPattern } from "../types/validation";

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

// Regular expressions for different validation types
const VALIDATION_PATTERNS = {
  email:
    /^[a-zA-Z0-9](?:[a-zA-Z0-9._%+-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/,
  phone: /^(\+27|0)[6-8][0-9]{8}$/,
  url: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
  zipcode: /^[0-9]{4}$/,
  username: /^[a-zA-Z0-9_-]{3,16}$/,
  password: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/,
  name: /^[a-zA-Z\s-']+$/,
  alphanumeric: /^[a-zA-Z0-9]+$/,
};

export const validateField = (field: Field, value: string | number | null | undefined): ValidationResult => {
  const errors: string[] = [];
  const stringValue = String(value || ""); // Ensure stringValue is never "undefined" or "null"

  // Required field validation
  // Use explicit null/undefined check instead of !value to avoid false positives
  // for valid falsy values like false (boolean) and 0 (number)
  const isValueEmpty = value === null || value === undefined || stringValue.trim() === "";
  if (field.required && isValueEmpty) {
    if (field.type === "signature-pad") {
      errors.push("Signature is required. Please provide a signature.");
    } else {
      errors.push("This field is required");
    }
  }

  // Type-specific validation (only if there's a value to validate)
  if (stringValue.length > 0 && field.validation) {

    let patternValidationFailed = false; // Flag to track pattern validation status

    // Pattern validation
    if (field.validation?.pattern) {
      let patternConfig: BaseValidationPattern | NumberFormatConfig | undefined;

      // First, check top-level patterns
      // Explicitly cast ValidationPatterns to a type that allows indexing by string
      const patterns = ValidationPatterns as Record<string, BaseValidationPattern | Record<string, NumberFormatConfig>>; // Explicitly type

      const topLevelPatternKey = Object.keys(patterns).find(
        (key) =>
          key !== 'numberFormats' &&
          (patterns[key] as BaseValidationPattern).pattern === field.validation!.pattern // Use non-null assertion
      );

      if (topLevelPatternKey) {
        patternConfig = patterns[topLevelPatternKey] as BaseValidationPattern;
      } else if (patterns.numberFormats) {
        // If not found in top-level, check number formats
        const numberFormats = patterns.numberFormats as Record<string, NumberFormatConfig>; // Explicitly type

        const numberFormatKey = Object.keys(numberFormats).find(
          (key) =>
            numberFormats[key]?.pattern ===
            field.validation!.pattern // Use non-null assertion
        );

        if (numberFormatKey) {
          patternConfig = numberFormats[numberFormatKey];
        }
      }

      if (patternConfig && !new RegExp(field.validation.pattern!).test(stringValue)) { // Use non-null assertion
        // Use specific error message if found, otherwise use a generic one
        if (patternConfig.errorMessage) {
          errors.push(patternConfig.errorMessage);
          patternValidationFailed = true; // Set flag if pattern validation fails with a specific message
        } else {
          errors.push("Invalid format");
          patternValidationFailed = true; // Set flag even for generic message
        }
      }
    }

    // Length constraints (apply to string representation) - Only check if pattern validation did NOT fail with a specific message
    if (!patternValidationFailed) {
        if (field.validation.minLength !== undefined && stringValue.length < field.validation.minLength) {
        errors.push(`Minimum length is ${field.validation.minLength} characters`);
        }
        if (field.validation.maxLength !== undefined && stringValue.length > field.validation.maxLength) {
        errors.push(`Maximum length is ${field.validation.maxLength} characters`);
        }
    }

    // Numeric and format validation (only if value is not empty after required check)
    if (stringValue.length > 0) {
      const numValue = parseFloat(stringValue); // Use parseFloat for consistent number conversion

      // Specific format validation (decimal, percentage)
      if (field.validation.format === "decimal") {
        const decimalRegex = /^-?\d+(\.\d+)?$/; // Correct regex for decimal
        if (!decimalRegex.test(stringValue) || isNaN(numValue)) { // Check both regex and isNaN
          errors.push("Please enter a valid decimal number");
        } else {
          // Apply min/max for valid decimal numbers if specified
          if (field.validation.min !== undefined && numValue < field.validation.min) {
            errors.push(`Value must be at least ${field.validation.min}`);
          }
          if (field.validation.max !== undefined && numValue > field.validation.max) {
            errors.push(`Value must be at most ${field.validation.max}`);
          }
        }
      } else if (field.validation.format === "percentage") {
        if (isNaN(numValue) || numValue < 0 || numValue > 100) {
          errors.push("Please enter a valid percentage between 0 and 100");
        } else {
          // Apply min/max for valid percentages if specified (often 0-100 handled by format, but possible)
          if (field.validation.min !== undefined && numValue < field.validation.min) {
            errors.push(`Value must be at least ${field.validation.min}`);
          }
          if (field.validation.max !== undefined && numValue > field.validation.max) {
            errors.push(`Value must be at most ${field.validation.max}`);
          }
        }
      } else {
        // General number validation for types without specific format like 'integer'
        // This check now only runs if format is NOT decimal or percentage
        if (field.validation.min !== undefined || field.validation.max !== undefined) {
          if (isNaN(numValue)) {
            errors.push("Please enter a valid number");
          } else {
            if (field.validation.min !== undefined && numValue < field.validation.min) {
              errors.push(`Value must be at least ${field.validation.min}`);
            }
            if (field.validation.max !== undefined && numValue > field.validation.max) {
              errors.push(`Value must be at most ${field.validation.max}`);
            }
          }
        }
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};